import React, { Component } from 'react';
import { FormikProps, withFormik, FormikValues } from 'formik';
import { ErrorContext, LabelFieldHorizontal, TextInput, Select, SpinnerCircle } from '@epicode/react-inspinia';
import { WithTranslation, withTranslation } from 'react-i18next';
import classNames from 'classnames';
import * as yup from 'yup';
import { OfferApplicationQuestionnaireRenderer } from './OfferApplicationQuestionnaireRenderer';
import { JobOfferQuestionnairePage } from '../../../../api/jobOffers/jobOffers.state';
import { RouteComponentProps, withRouter } from 'react-router';
import { SubmitJobApplicationCommandError } from '../../../../api/jobOfferApplication/jobOfferApplication.thunk';
import {
    QuestionQuestionnaireElementType,
    QuestionnaireAnswersDto,
    QuestionnaireElementType,
    QuestionnaireElement,
    QuestionQuestionnaireAnswerType,
} from '../../../../api/jobOffers/jobOffers.dto';
import { FileUploadValue } from '@epicode/react-inspinia/dist/components/form/field/file/FileUpload';

export const prefixes: Record<string, string[]> = {
    en: ['Mr.', 'Mrs.', 'Ms.', 'Miss', 'prefer not to say'],
    pl: ['Pan', 'Pani', 'Panna', 'nie chcę ujawniać'],
};

interface QuestionnairePageProps {
    page: JobOfferQuestionnairePage;
    storedAnswers: QuestionnaireAnswersDto;
    isLastPage: boolean;
    isSubmittingApplication: boolean;
    validationSchema: yup.Schema<any>;
    formErrors: SubmitJobApplicationCommandError[];
    onSubmit: (values: FormikValues) => Promise<void>;
    onBack: (values: FormikValues) => void;
}

interface QuestionnairePageRouterProps {
    id: string;
}

class QuestionnairePage extends Component<
    QuestionnairePageProps &
        WithTranslation &
        RouteComponentProps<QuestionnairePageRouterProps> &
        FormikProps<Record<string, any>>
> {
    private questionnaireRenderer = new OfferApplicationQuestionnaireRenderer<Record<string, any>>(
        { ...this.props },
        this.props.t,
        this.props.match.params.id
    );

    static defaultProps = {
        isLastPage: true,
    };

    componentDidUpdate() {
        this.questionnaireRenderer.setFormikProps({ ...this.props });
    }

    render() {
        const { errors, touched, handleSubmit } = this.props;

        return (
            <ErrorContext.Provider value={{ errors: errors, touched }}>
                <form onSubmit={handleSubmit}>
                    <div className='ibox mt-4 mb-0'>
                        <div className='ibox-content pt-4 pb-4 pl-5 pr-5'>
                            {this.hasStaticElements && this.renderStaticElements()}
                            {this.renderQuestionnaireElements()}
                        </div>
                    </div>
                    {this.renderButtons()}
                </form>
            </ErrorContext.Provider>
        );
    }

    private renderQuestionnaireElements = () => {
        return this.questionnaireRenderer.renderQuestionnaireElements(
            this.props.page.elements,
            this.props,
            this.props.t
        );
    };

    private get hasStaticElements(): boolean {
        return this.props.page.pageNumber === 1;
    }

    private renderStaticElements = () => {
        const { t, i18n, values, handleChange, handleBlur } = this.props;
        const lang = i18n.language;
        const prefixesInLang = prefixes[lang] || prefixes.en;

        return (
            <>
                <h3 className='font-weight-bold text-dark mb-4'>{t('jobApplication.personalInformation')}</h3>
                <div className='row flex-fill'>
                    <div className='col-6'>
                        <LabelFieldHorizontal for='firstName' label={t('jobApplication.fullName')}>
                            <TextInput
                                name='firstName'
                                value={values.firstName || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={t('jobApplication.firstName')}
                            />
                        </LabelFieldHorizontal>
                    </div>
                    <div className='col-6'>
                        <LabelFieldHorizontal for='lastName' label={<>&nbsp;</>}>
                            <TextInput
                                name='lastName'
                                value={values.lastName || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={t('jobApplication.lastName')}
                            />
                        </LabelFieldHorizontal>
                    </div>
                </div>
                <div className='row flex-fill'>
                    <div className='col-6'>
                        <LabelFieldHorizontal for='prefix' label={t('jobApplication.titlePrefix')}>
                            <Select name='prefix' value={values.prefix} onChange={handleChange} onBlur={handleBlur}>
                                <option value=''>{t('jobApplication.selectFromList')}</option>
                                {prefixesInLang.map(this.renderPrefixOption)}
                            </Select>
                        </LabelFieldHorizontal>
                    </div>
                    <div className='col-6'>
                        <LabelFieldHorizontal for='phoneNumber' label={t('jobApplication.phone')}>
                            <TextInput
                                name='phoneNumber'
                                value={values.phoneNumber || ''}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder={t('jobApplication.phonePlaceholder')}
                            />
                        </LabelFieldHorizontal>
                    </div>
                </div>
                <LabelFieldHorizontal for='email' label={t('jobApplication.email')}>
                    <TextInput
                        name='email'
                        value={values.email || ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder={t('jobApplication.emailPlaceholder')}
                        type='email'
                    />
                </LabelFieldHorizontal>
            </>
        );
    };

    private renderButtons = () => {
        const { t, isLastPage } = this.props;
        const { pageNumber } = this.props.page;
        let submitLabel = t('jobApplication.saveAndContinue');

        if (this.props.isSubmittingApplication) {
            submitLabel = t('jobApplication.sending');
        } else if (isLastPage) {
            submitLabel = t('jobApplication.sendApplication');
        }

        return (
            <div className='d-flex justify-content-between mt-4 mb-5'>
                {pageNumber > 1 ? (
                    <button
                        type='button'
                        className={classNames('btn btn-default btn-lg btn-w-m font-weight-bold')}
                        onClick={this.onBackButtonClick}
                        disabled={this.props.isSubmittingApplication}>
                        {t('jobApplication.back')}
                    </button>
                ) : (
                    <>&nbsp;</>
                )}
                <button
                    className={classNames(
                        'btn btn-primary btn-lg font-weight-bold d-flex align-items-center justify-content-center'
                    )}
                    type='submit'
                    disabled={this.props.isSubmittingApplication}>
                    {this.props.isSubmittingApplication && (
                        <SpinnerCircle spinnerSize='small' className='mt-0 ml-0 mb-0 mr-2' />
                    )}
                    {submitLabel}
                </button>
            </div>
        );
    };

    private renderPrefixOption = (prefix: string) => (
        <option key={prefix} value={prefix}>
            {prefix}
        </option>
    );

    private onBackButtonClick = () => {
        this.props.onBack(this.props.values);
    };
}

export default withFormik({
    enableReinitialize: true,
    validationSchema: (props: QuestionnairePageProps) => props.validationSchema,
    mapPropsToValues: (props: QuestionnairePageProps) => {
        const result: Record<string, any> = {};
        // set initial values to empty ones - required by formik to actually validate the fields
        props.page.elements.forEach((el) => {
            const storedAnswer = getStoredAnswerForField(el, props.storedAnswers);

            result[el.id] = storedAnswer || getDefaultValueForFieldType(el.$type);
        });

        // on first page add static fields
        if (props.page.pageNumber === 1) {
            result.firstName = props.storedAnswers.firstName || '';
            result.lastName = props.storedAnswers.lastName || '';
            result.prefix = props.storedAnswers.prefix || '';
            result.phoneNumber = props.storedAnswers.phoneNumber || '';
            result.email = props.storedAnswers.email || '';
        }

        return result;
    },
    mapPropsToErrors: (props: QuestionnairePageProps) => {
        if (props.isSubmittingApplication) {
            return {};
        }

        return props.formErrors.reduce(formErrorsToFormikErrors, {} as Record<string, any>);
    },
    mapPropsToTouched: (props: QuestionnairePageProps) =>
        props.formErrors.reduce(formErrorsToFormikTouched, {} as Record<string, any>),
    handleSubmit: (values: FormikValues, { props }) => {
        props.onSubmit(values);
    },
})(withRouter(withTranslation()(QuestionnairePage)));

const formErrorsToFormikErrors = (prev: Record<string, any>, cur: SubmitJobApplicationCommandError) => ({
    ...prev,
    [cur.causedBy]: cur.errorMessage,
});

const formErrorsToFormikTouched = (prev: Record<string, any>, cur: SubmitJobApplicationCommandError) => ({
    ...prev,
    [cur.causedBy]: true,
});

function getStoredAnswerForField(element: QuestionnaireElement, storedAnswers: QuestionnaireAnswersDto) {
    const answer = storedAnswers.additionalQuestionnaireAnswers.find(
        (storedAnswer) => storedAnswer.questionId === element.id
    );

    if (!answer) {
        return undefined;
    }

    switch (answer.$type) {
        case QuestionQuestionnaireAnswerType.QuestionnaireAddressAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireMultiLineTextAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireSingleLineTextAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireSummaryAnswer:
            return answer.value;
        case QuestionQuestionnaireAnswerType.QuestionnaireAttachmentAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireCoverLetterAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnairePhotoAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireResumeAnswer:
            return { fileId: answer.fileIdentifier, fileName: answer.fileName } as FileUploadValue;
        case QuestionQuestionnaireAnswerType.QuestionnaireEducationAnswer:
        case QuestionQuestionnaireAnswerType.QuestionnaireExperienceAnswer:
            return answer.values;
        case QuestionQuestionnaireAnswerType.QuestionnaireMultipleChoiceAnswer:
            return answer.chosenOptions;
        case QuestionQuestionnaireAnswerType.QuestionnaireSingleChoiceAnswer:
            return answer.chosenOption;
    }
}

function getDefaultValueForFieldType(type: QuestionnaireElementType) {
    switch (type) {
        case QuestionQuestionnaireElementType.QuestionnaireMultipleChoice:
            return [];
        case QuestionQuestionnaireElementType.QuestionnaireEducation: {
            return [
                {
                    school: '',
                    degree: '',
                    fieldOfStudy: '',
                    currentlyStudyingHere: false,
                    description: '',
                    startDate: '',
                    startDateMonth: '',
                    startDateYear: '',
                    endDate: '',
                    endDateMonth: '',
                    endDateYear: '',
                    order: 1,
                },
            ];
        }
        case QuestionQuestionnaireElementType.QuestionnaireExperience: {
            return [
                {
                    title: '',
                    company: '',
                    currentlyWorkingInThisRole: false,
                    description: '',
                    startDate: '',
                    startDateMonth: '',
                    startDateYear: '',
                    endDate: '',
                    endDateMonth: '',
                    endDateYear: '',
                    locationDescription: '',
                    order: 1,
                },
            ];
        }
        default:
            return '';
    }
}
